import React, { type PropsWithChildren } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import {
  FallbackImage,
  IllusEmpty_2,
  theme,
  useNavbarContainerHeight,
  useResponsive,
} from '@sg/garnish';

export const ForgotToScanFormContainer = (props: ForgotToScanFormContainerProps) => {
  const { children, image, imageA11y, withNavbarSpacing = false } = props;

  const { currentBreakpoint } = useResponsive();
  const { height: navbarHeight } = useNavbarContainerHeight();

  // ─── Styles ────────────────────────────────────────────────────────────────

  const formContentNavbarSpacingStyle = useStyle(() => {
    if (!withNavbarSpacing) return;

    return { paddingTop: theme.spacing['20'] + navbarHeight };
  }, [navbarHeight, withNavbarSpacing]);

  const formContentStyles = [styles.formContent, formContentNavbarSpacingStyle];

  // ───────────────────────────────────────────────────────────────────────────

  if (currentBreakpoint.isXS) {
    return (
      <ScrollView
        style={styles.formContainer}
        contentContainerStyle={styles.grow}
        automaticallyAdjustKeyboardInsets
      >
        {children}
      </ScrollView>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.imageContainer}>
        <FallbackImage
          style={styles.image}
          baseUrl={image}
          aria-label={imageA11y}
          defaultImage={IllusEmpty_2}
          contentfulOptions={contentfulOptions}
        />
      </View>

      <ScrollView style={styles.formContainer} contentContainerStyle={formContentStyles}>
        {children}
      </ScrollView>
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const contentfulOptions = {
  fm: 'webp' as const,
  q: 70,
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  grow: {
    flexGrow: 1,
  },
  imageContainer: {
    flex: 1,
  },
  image: {
    width: '100%',
    height: '100%',
  },
  formContainer: {
    flex: 1,
  },
  formContent: {
    flexGrow: 1,
    maxWidth: 480,
    alignSelf: 'center',
    paddingTop: theme.spacing['20'],
    paddingHorizontal: theme.spacing['4'],
    gap: theme.spacing['10'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ForgotToScanFormContainerProps = {
  children: PropsWithChildren['children'];
  image?: string;
  imageA11y?: string;
  withNavbarSpacing?: boolean;
};
